// DiagnoseDialog — modal that runs an AI weak-point diagnosis + shows the
// verdict for save.
//
// 19BO+ Line 2 B4.3 · counterpart to /ingest/diagnose. Built on PhaseDialog
// (refactor: shares the confirm/running/review/error base with evaluate-dialog).
//
// diagnose-specific logic (vs evaluate):
//   - props carry aggregate stats (DiagnoseRequest), not a single item + answer
//   - confirm phase disables the button when stats.total===0 (frontend guard
//     against the backend's 400 stats_empty — saves a round-trip)
//   - review renders the read-only DiagnoseVerdictCard
//   - onDiagnosed fires only when the user accepts (铁律3)

import { Sparkles } from 'lucide-react';
import { useT } from '../i18n/use-t.js';
import { usePreflightCredits, ESTIMATED_POINTS } from '../lib/hooks/use-preflight-credits.js';
import { postDiagnose, mapDiagnoseErrorToMessage } from '../api/diagnose.js';
import type { DiagnoseVerdict } from '@examforge/shared/schemas';
import type { DiagnoseRequest } from '@examforge/shared/api';
import { DiagnoseVerdictCard } from './cards/diagnose-verdict-card.js';
import { PhaseDialog, type PhaseDialogConfirmCtx } from './phase-dialog.js';

export interface DiagnoseDialogProps {
  open: boolean;
  onClose(): void;
  /** The aggregate wrong-answer stats to diagnose (from wrongAnswerStats()).
   *  When total===0, the confirm button is disabled (no data to diagnose). */
  stats: DiagnoseRequest;
  /** Called with the verdict ONLY when the user accepts in the review phase.
   *  The host view persists it into progress.diagnosis. */
  onDiagnosed(verdict: DiagnoseVerdict): void;
}

export function DiagnoseDialog({
  open, onClose, stats, onDiagnosed,
}: DiagnoseDialogProps): React.ReactNode {
  const t = useT();
  const preflight = usePreflightCredits();

  // Frontend guard: no wrong answers → nothing to diagnose. Disables the confirm
  // button so we never waste a round-trip on the backend's 400 stats_empty.
  const statsEmpty = stats.total === 0;

  function start(ctx: PhaseDialogConfirmCtx): void {
    if (stats.total === 0) return;
    if (!preflight(ESTIMATED_POINTS.diagnose)) return;
    ctx.startAsync(
      async () => {
        const result = await postDiagnose(stats);
        return result.verdict;
      },
      (verdict) => <DiagnoseVerdictCard verdict={verdict} />,
      mapDiagnoseErrorToMessage,
    );
  }

  return (
    <PhaseDialog<DiagnoseVerdict>
      open={open}
      onClose={onClose}
      title={t('diagnose.title')}
      ariaLabel={t('diagnose.ariaLabel')}
      loadingLabel={t('diagnose.loading')}
      headerIcon={<Sparkles size={16} aria-hidden="true" />}
      renderConfirm={(ctx) => (
        <>
          <p className="phase-dialog-dialog-cost-hint">{t('diagnose.costHint')}</p>
          <div className="phase-dialog-dialog-actions">
            <button type="button" className="btn-secondary" onClick={ctx.close}>
              {t('diagnose.cancel')}
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={() => start(ctx)}
              disabled={statsEmpty}
            >
              <Sparkles size={13} aria-hidden="true" /> {t('diagnose.confirm')}
            </button>
          </div>
        </>
      )}
      renderReview={(verdict, ctx) => (
        <>
          <p className="phase-dialog-dialog-review-hint">{t('diagnose.reviewHint')}</p>
          <DiagnoseVerdictCard verdict={verdict} />
          <div className="phase-dialog-dialog-actions">
            <button type="button" className="btn-secondary" onClick={ctx.close}>
              {t('diagnose.cancel')}
            </button>
            <button type="button" className="btn-primary" onClick={() => { onDiagnosed(verdict); ctx.accept(); }}>
              {t('diagnose.accept')}
            </button>
          </div>
        </>
      )}
      renderError={(message, ctx) => (
        <div className="phase-dialog-dialog-actions">
          <button type="button" className="btn-secondary" onClick={ctx.close}>
            {t('diagnose.cancel')}
          </button>
          <button type="button" className="btn-primary" onClick={ctx.retry}>
            {t('diagnose.retry')}
          </button>
        </div>
      )}
    />
  );
}
